import React, { useState, useCallback } from "react";
import security from '../assets/icons/security.svg'
import back from '../assets/icons/arrow_back.svg'
import arrow from '../assets/icons/arrow_right.svg'
import tape from '../assets/icons/yellow_rectangle.svg'
import mail from '../assets/icons/mail.svg'
import psw from '../assets/icons/password.svg'
import '../assets/css/layout/auth.css'
import FormInput from "../components/elements/form_input";
import Button from "../components/elements/button";
import { showError, showSuccess } from "../components/elements/customError";
import { useNavigate, Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { useAuth } from "../context/AuthContext";

const Login = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);

    const handleSubmit = useCallback(async (e) => {
        // Prevent default form submission behavior
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        
        // Prevent multiple submissions
        if (loading) return;
        
        // Validate fields
        if (!email || !password) {
            showError("Please fill in all fields");
            return;
        }

        // Basic email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
            showError("Please enter a valid email address");
            return;
        }

        setLoading(true);
        
        try {
            const response = await login({ email, password });
            
            // Only proceed if login was successful
            if (response) {
                // Store remember me preference
                if (rememberMe) {
                    localStorage.setItem('rememberMe', 'true');
                } else {
                    localStorage.removeItem('rememberMe');
                }
                
                showSuccess("Login successful!");
                
                // Get user data and redirect based on role
                const userData = response.user || JSON.parse(localStorage.getItem('user'));
                
                // Small delay to show success message before navigation
                setTimeout(() => {
                    if (userData?.is_doctor) {
                        navigate('/vet', { replace: true });
                    } else if (userData?.is_vendor) {
                        navigate('/vendor', { replace: true });
                    } else if (userData?.is_lab) {
                        navigate('/lab', { replace: true });
                    } else if (userData?.is_admin) {
                        navigate('/admin', { replace: true });
                    } else {
                        navigate('/dashboard', { replace: true });
                    }
                }, 500);
            }
        } catch (error) {
            console.error('Login error:', error);
            const errorMessage = error.response?.data?.message 
                || error.response?.data?.detail
                || error.response?.data?.error
                || error.displayMessage
                || error.message
                || "Login failed. Please check your credentials and try again.";
            showError(errorMessage);
            // Keep user on the same page - no navigation on error
        } finally {
            setLoading(false);
        }
    }, [email, password, loading, login, navigate, rememberMe]);

    return (
        <React.Fragment>
            <ToastContainer />
            <div className="auth-container">
                <section className="auth-right">
                    <div className="right-inner">
                        <h3 className="right-inner-title">A platform tailored for the Modern Fish Farmer</h3>
                        <p className="right-inner-text">
                            Access expert veterinary care, laboratory services, and a marketplace 
                            designed specifically for aquaculture professionals. Join thousands of 
                            fish farmers improving their operations with E-AquaHealth.
                        </p>
                        <button className="learn-more-btn" onClick={() => navigate('/about')}>
                            Learn More &nbsp; <img src={arrow} alt="arrow" className="arrow_up"/>
                        </button>
                        <img src={tape} alt="" className="tape" />
                    </div>
                </section>
                <section className="auth-section">
                    <div className="auth-logo-section">
                        <Link to="/" style={{ textDecoration: 'none', color: 'inherit' }}>
                            E-AQUAHEALTH
                        </Link>
                        <img src={security} alt="security"/>
                    </div>
                    <Link to="/" className="back-home-link">
                        <img src={back} alt="back"/> &nbsp; Back to Home
                    </Link>
                    <div className="spacer"></div>
                    <h3 className="auth-title">Welcome Back</h3>
                    <p className="auth-subtitle">Sign in to continue to your dashboard</p>
                    
                    <form className="auth-form" onSubmit={handleSubmit} action="#">
                        <FormInput 
                            id="email" 
                            label="Email"
                            required={true}
                            icon={<img src={mail} alt="user-email"/>}
                            placeholder="Enter your email" 
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            name="email"
                            type="email"
                            disabled={loading}
                            autoComplete="email"
                        />

                        <FormInput 
                            id="password" 
                            label="Password"
                            required={true}
                            icon={<img src={psw} alt="password"/>}
                            placeholder="Enter your password" 
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            name="password"
                            type="password"
                            disabled={loading}
                            autoComplete="current-password"
                        />

                        <div className="login-options">
                            <label className="remember-me">
                                <input 
                                    type="checkbox" 
                                    checked={rememberMe}
                                    onChange={(e) => setRememberMe(e.target.checked)}
                                    disabled={loading}
                                />
                                <span>Remember me</span>
                            </label>
                            <Link to="/forgot-password" className="forgot-password-link">
                                Forgot Password?
                            </Link>
                        </div>

                        <Button 
                            text={loading ? "Signing in..." : "Sign In"}
                            type="submit" 
                            fullWidth 
                            icon={!loading && <img src={arrow} alt="" className="arrow_up" />}
                            disabled={loading}
                            loading={loading}
                        />

                        <p className="auth-text">
                            Don&apos;t have an Account?{' '}
                            <Link to="/register">Click here to get started</Link>
                        </p>

                        <div className="auth-divider">
                            <span>or</span>
                        </div>

                        <div className="professional-signup">
                            <p className="professional-text">Are you a professional?</p>
                            <div className="professional-links">
                                <Link to="/vet/register" className="professional-link">
                                    Register as Vet
                                </Link>
                                <Link to="/aht/register" className="professional-link">
                                    Register as Animal Health Technologist
                                </Link>
                                <Link to="/lab/register" className="professional-link">
                                    Register as Lab
                                </Link>
                                <Link to="/vendor/register" className="professional-link">
                                    Register as Vendor
                                </Link>
                                <Link to="/logistics/register" className="professional-link">
                                    Register as Logistics
                                </Link>
                            </div>
                        </div>
                    </form>

                    <ul className="footer-links">
                        <li className="footer-link-item">
                            <Link to="/contact">Contact Us</Link>
                        </li>
                        <li className="footer-link-item">
                            <Link to="/terms">Terms of Service</Link>
                        </li>
                        <li className="footer-link-item">
                            <Link to="/faq">FAQs</Link>
                        </li>
                    </ul>
                </section>
            </div>
        </React.Fragment>
    );
}

export default Login;